<!--
  CreateStepperDialog: frame of the "… anlegen" dialogs in the style of the sectioned edit dialogs (Standort, Schüler:in,
  Eltern, Mitarbeiter:in bearbeiten): numbered steps on the left (pill tabs on phones) that follow the scroll position,
  a check mark once the required fields of a step are filled, "Weiter" to the next step, sticky footer and
  "Eingaben verwerfen?" when the dialog is closed with entries. Only presentation: the dialogs keep their own logic.
  Props: value, title, subtitle, icon, tone, sections ({ id, title, icon, complete?, hint? }), loading, dirty (ask before
         closing), saveText, saveIcon, saveDisabled, saveDisabledReason, hideSave, cancelText, persistent, maxWidth
  Slots: default (the <section class="mdlg-section" data-section="<id>"> blocks), footer-start, footer-actions (extra buttons
         left of the primary one), after-body (e.g. nested dialogs)
  Events: close (the user closes – after the discard confirmation when dirty), save
  Methods (via ref): scrollTo(id), scrollToFirstError()
-->
<template>
  <v-dialog :fullscreen="$vuetify.breakpoint.smAndDown" :max-width="maxWidth" :persistent="loading || dirty || persistent" :value="value"
            content-class="acq-dialog" scrollable @click:outside="onOutside" @input="onDialogInput" @keydown.esc="onOutside">
    <v-card class="acq-dialog mdlg-scope eid csd">
      <div class="mdlg-header">
        <span :class="`tone-${tone}`" class="acq-avatar acq-avatar--sm"><v-icon small style="color: inherit">{{ icon }}</v-icon></span>
        <div class="mdlg-header__text">
          <div class="acq-section-title">{{ title }}</div>
          <div v-if="subtitle" class="acq-tiny text-truncate">{{ subtitle }}</div>
        </div>
        <v-btn :disabled="loading" aria-label="Schließen" icon @click="requestClose"><v-icon>mdi-close</v-icon></v-btn>
      </div>
      <nav v-if="$vuetify.breakpoint.smAndDown && sections.length > 1" aria-label="Schritte" class="eid-tabs">
        <button v-for="(section, index) in sections" :key="section.id" :class="{ 'pill--active': activeSection === section.id }" class="pill"
                type="button" @click="scrollTo(section.id)">
          <v-icon v-if="section.complete" x-small>mdi-check</v-icon><span v-else>{{ index + 1 }}.</span>&nbsp;{{ section.title }}
        </button>
      </nav>
      <v-card-text ref="scroller" class="mdlg-body eid-body" @focusin="onFocusIn" @focusout="onFocusOut"
                   @scroll="onScroll">
        <div class="eid-layout">
          <nav v-if="$vuetify.breakpoint.mdAndUp && sections.length > 1" aria-label="Schritte" class="eid-nav">
            <button v-for="(section, index) in sections" :key="section.id" :aria-current="activeSection === section.id ? 'step' : undefined"
                    :class="{ 'csd-step--active': activeSection === section.id, 'csd-step--complete': section.complete }"
                    class="eid-nav__item csd-step" type="button" @click="scrollTo(section.id)">
              <span class="csd-step__num">
                <v-icon v-if="section.complete" x-small>mdi-check</v-icon>
                <template v-else>{{ index + 1 }}</template>
              </span>
              <span class="csd-step__text">
                <span class="csd-step__title">{{ section.title }}</span>
                <span v-if="section.hint" class="csd-step__hint">{{ section.hint }}</span>
              </span>
            </button>
          </nav>
          <div class="eid-content">
            <slot/>
          </div>
        </div>
      </v-card-text>
      <div class="mdlg-footer">
        <slot name="footer-start">
          <button v-if="nextSection" :disabled="loading" class="add-link" type="button" @click="scrollTo(nextSection.id)">
            Weiter: {{ nextSection.title }}<v-icon x-small>mdi-arrow-down</v-icon>
          </button>
          <span v-else-if="sections.length > 1" class="acq-tiny">{{ completeText }}</span>
        </slot>
        <v-spacer/>
        <button :disabled="loading" class="cta cta--ghost" type="button" @click="requestClose">{{ cancelText }}</button>
        <slot name="footer-actions"/>
        <span v-if="!hideSave" v-tip="saveDisabled && !loading ? saveDisabledReason : ''" class="app-tip-wrap">
          <button :disabled="loading || saveDisabled" class="cta cta--primary" type="button" @click="$emit('save')">
            <v-progress-circular v-if="loading" class="cta__icon" indeterminate size="14" width="2"/>
            <v-icon v-else-if="saveIcon" class="cta__icon" small>{{ saveIcon }}</v-icon>
            {{ saveText }}
          </button>
        </span>
      </div>
    </v-card>
    <discard-changes-dialog v-model="showDiscard" text="Die Eingaben wurden noch nicht gespeichert. Beim Schließen gehen sie verloren."
                            title="Eingaben verwerfen?" @confirm="confirmDiscard"/>
    <slot name="after-body"/>
  </v-dialog>
</template>

<script lang="ts">
import Vue from 'vue';
import {createScrollSpy, ScrollSpy} from '@/helpers/sectionScrollSpy.controller';
import DiscardChangesDialog from '@/components/common/modern/discardChangesDialog.vue';

export type CreateStepperSection = { id: string; title: string; icon?: string; complete?: boolean; hint?: string };

export default Vue.extend({
  components: {DiscardChangesDialog},
  props: {
    value: {type: Boolean, required: true},
    title: {type: String, required: true},
    subtitle: {type: String, default: ''},
    icon: {type: String, default: 'mdi-plus'},
    tone: {type: String, default: 'info'},
    sections: {type: Array as () => CreateStepperSection[], default: () => []},
    loading: {type: Boolean, default: false},
    /** Entries were made: ask "Eingaben verwerfen?" before closing */
    dirty: {type: Boolean, default: false},
    saveText: {type: String, default: 'Anlegen'},
    saveIcon: {type: String, default: 'mdi-check'},
    saveDisabled: {type: Boolean, default: false},
    saveDisabledReason: {type: String, default: ''},
    hideSave: {type: Boolean, default: false},
    cancelText: {type: String, default: 'Abbrechen'},
    /** Never closed by a click outside / Esc (e.g. a result view); X and the cancel button still emit close */
    persistent: {type: Boolean, default: false},
    maxWidth: {type: [Number, String], default: 1040},
  },
  data: () => ({
    activeSection: '',
    spy: null as ScrollSpy | null,
    showDiscard: false,
  }),
  computed: {
    sectionIds(): string {
      return this.sections.map((section) => section.id).join('|');
    },
    nextSection(): CreateStepperSection | undefined {
      const index = this.sections.findIndex((section) => section.id === this.activeSection);
      return index >= 0 && index < this.sections.length - 1 ? this.sections[index + 1] : undefined;
    },
    completeText(): string {
      const missing = this.sections.filter((section) => section.complete === false).length;
      if (missing === 0) {
        return 'Alle Pflichtangaben ausgefüllt';
      }
      return missing === 1 ? '1 Schritt mit offenen Pflichtangaben' : `${missing} Schritte mit offenen Pflichtangaben`;
    },
  },
  methods: {
    scroller(): HTMLElement | undefined {
      const ref = this.$refs.scroller as Vue | HTMLElement | undefined;
      return ref && '$el' in ref ? ref.$el as HTMLElement : ref as HTMLElement | undefined;
    },
    scrollTo(id: string) {
      this.spy?.scrollTo(id);
    },
    /** Shows the first invalid field after a failed validation */
    scrollToFirstError() {
      this.$nextTick(() => {
        const scroller = this.scroller();
        const field = scroller?.querySelector('.v-input.error--text, .error--text') as HTMLElement | null | undefined;
        const section = field?.closest('[data-section]') as HTMLElement | null | undefined;
        if (section?.dataset.section) {
          this.scrollTo(section.dataset.section);
        }
      });
    },
    /** Scroll spy (sectionScrollSpy.helper.ts): focused field's section, else the last section above the anchor line, the last at the bottom */
    onScroll() {
      this.spy?.onScroll();
    },
    onFocusIn(event: FocusEvent) {
      this.spy?.onFocusIn(event);
    },
    onFocusOut(event: FocusEvent) {
      this.spy?.onFocusOut(event);
    },
    /** Persistent while dirty: a click outside / Esc asks before discarding (otherwise v-dialog closes via input) */
    onOutside() {
      if (this.dirty && !this.loading) {
        this.showDiscard = true;
      }
    },
    onDialogInput(v: boolean) {
      if (!v) {
        this.requestClose();
      }
    },
    requestClose() {
      if (this.loading) {
        return;
      }
      if (this.dirty) {
        this.showDiscard = true;
        return;
      }
      this.$emit('close');
    },
    confirmDiscard() {
      this.showDiscard = false;
      this.$emit('close');
    },
  },
  created() {
    this.spy = createScrollSpy({scroller: () => this.scroller(), onChange: (id) => {
      this.activeSection = id;
    }});
  },
  beforeDestroy() {
    this.spy?.destroy();
  },
  watch: {
    /** Conditional steps appear / disappear: re-evaluate the active one */
    sectionIds() {
      this.$nextTick(() => this.spy?.refresh());
    },
    value: {
      immediate: true,
      handler(v: boolean) {
        if (v) {
          this.showDiscard = false;
          this.spy?.reset();
          this.activeSection = this.sections[0]?.id || '';
          this.$nextTick(() => {
            const scroller = this.scroller();
            if (scroller) {
              scroller.scrollTop = 0;
            }
          });
        }
      },
    },
  },
});
</script>

<style src="@/components/acquisitionList/ui/acquisitionViews.css"></style>
<style src="@/components/common/modern/modern.css"></style>
<style src="@/components/common/modern/modernDialog.css"></style>
<style src="@/components/common/modern/editDialog.css"></style>
<style src="@/components/timetable2/dialogs/calDialog.css"></style>
<style>
.csd .csd-step {
  height: auto;
  min-height: 40px;
  padding: 6px 10px;
  align-items: center;
}

.csd-step__num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: rgba(15, 23, 42, .07);
  font-size: 12px;
  font-weight: 600;
}

.csd-step__text {
  display: flex;
  flex-direction: column;
  min-width: 0;
  text-align: left;
}

.csd-step__title {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.csd-step__hint {
  font-size: 11px;
  font-weight: 400;
  opacity: .75;
}

.csd .csd-step--active {
  background: #E3F2FD;
  color: #0D47A1;
}

.csd .csd-step--active .csd-step__num {
  background: #1976D2;
  color: #FFFFFF;
}

.csd .csd-step--complete .csd-step__num {
  background: #E6F4EA;
  color: #1B7F3B;
}

.csd .csd-step--complete .csd-step__num .v-icon {
  color: inherit !important;
}

.theme--dark .csd .csd-step--active {
  background: rgba(100, 181, 246, .16);
  color: #90CAF9;
}

.theme--dark .csd-step__num {
  background: rgba(255, 255, 255, .1);
}
</style>
